import { useMemo } from 'react';
import {
  isSameDay as dfIsSameDay,
  isSameMonth as dfIsSameMonth,
  isSameYear as dfIsSameYear,
  isAfter as dfIsAfter,
  isBefore as dfIsBefore,
  startOfDay as dfStartOfDay,
  endOfDay as dfEndOfDay,
  addDays as dfAddDays,
  addHours as dfAddHours,
  subDays as dfSubDays,
  startOfMonth as dfStartOfMonth,
  endOfMonth as dfEndOfMonth,
  addMonths as dfAddMonths,
  addYears as dfAddYears,
  subMonths as dfSubMonths,
  subYears as dfSubYears,
  startOfHour as dfStartOfHour,
  sub as dfSub,
  isWithinInterval as dfIsWithinInterval,
  differenceInCalendarDays as dfDifferenceInCalendarDays,
  getUnixTime,
  fromUnixTime,
  startOfWeek,
} from 'date-fns';
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import { useAuthContext } from '/src/hooks';
import {
  DEFAULT_DATE_RANGE_POLICIES,
  MAX_ALLOWABLE_DATE_RANGES,
  SPECIAL_DATE_RANGES,
  TIME_UNITS,
} from '/src/features/_global/constants';

const { TODAY, YESTERDAY, RECENT, LAST_7_DAYS, LAST_30_DAYS, LAST_100_DAYS, LAST_MONTH, THIS_MONTH } =
  SPECIAL_DATE_RANGES;

const singleDay = 60 * 60 * 24;
const SUPPORTED_SPECIAL_DATE_RANGES = new Set([
  TODAY,
  YESTERDAY,
  RECENT,
  LAST_7_DAYS,
  LAST_30_DAYS,
  LAST_100_DAYS,
  THIS_MONTH,
  LAST_MONTH,
]);

export function useTimeZonedFunctions(forcedTimeZone) {
  const { currentUser } = useAuthContext();

  // dummy for now, maybe we'll do it per site instead. Point is we'll need to be in a hook anyhow.
  const timeZone = forcedTimeZone ?? currentUser?.settings?.timezone ?? 'UTC';

  if (!isValidTimeZone(timeZone)) throw new Error(`Invalid time zone: ${timeZone}`);

  const helpers = useMemo(() => {
    function format(date, format, options) {
      const parsedDate = toDate(date);
      return formatInTimeZone(parsedDate, timeZone, format, options);
    }

    function formatDay(date) {
      return format(date, 'MMM d, yyyy');
    }

    function formatDayFull(date) {
      return format(date, 'EEE, MMM d, yyyy');
    }

    function formatDateTime(date) {
      return format(date, `MMM d, h:mm aaa`);
    }

    function formatDateTimeFull(date) {
      return format(date, `MMM d, y, h:mm aaa`);
    }

    function formatRangeSameMonth(start, end) {
      return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
    }

    function formatRangeSameYear(start, end) {
      return `${format(start, 'MMM d')} - ${formatDay(end)}`;
    }

    function formatRangeDifferentYear(start, end) {
      return `${formatDay(start)} - ${formatDay(end)}`;
    }

    function formatChartLabel(date, timeUnit = TIME_UNITS.DAY) {
      if (timeUnit === TIME_UNITS.MONTH) {
        return format(date, 'MMM yyyy');
      }
      return format(date, 'MMM d');
    }

    function formatChartTooltip(start, end, timeUnit = TIME_UNITS.DAY) {
      if (timeUnit === TIME_UNITS.DAY) {
        return format(start, 'EEE, MMM d');
      }
      if ([TIME_UNITS.WEEK, TIME_UNITS.MONTH].includes(timeUnit)) {
        if (isSameDay(start, end)) {
          return `${format(start, 'MMM d')}`;
        }
        if (isSameMonth(start, end)) {
          return `${format(start, 'MMM d')} - ${format(end, 'd')}`;
        }
        return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`;
      }
      throw new Error(`Invalid timeUnit: ${timeUnit}`);
    }

    function isSameDay(left, right) {
      return compareInTz(left, right, timeZone, dfIsSameDay);
    }

    function isSameMonth(left, right) {
      return compareInTz(left, right, timeZone, dfIsSameMonth);
    }

    function isSameYear(left, right) {
      return compareInTz(left, right, timeZone, dfIsSameYear);
    }

    function isAfter(left, right) {
      const zLeft = toZonedDate(left, timeZone);
      const zRight = toZonedDate(right, timeZone);
      return dfIsAfter(zLeft, zRight);
    }

    function isBefore(left, right) {
      const zLeft = toZonedDate(left, timeZone);
      const zRight = toZonedDate(right, timeZone);
      return dfIsBefore(zLeft, zRight);
    }

    function startOfDay(input) {
      return mapInTz(input, timeZone, (d) => dfStartOfDay(d));
    }

    function endOfDay(input) {
      return mapInTz(input, timeZone, (d) => dfEndOfDay(d));
    }

    function addDays(input, amount) {
      return mapInTz(input, timeZone, (d) => dfAddDays(d, amount));
    }

    function addHours(input, amount) {
      return mapInTz(input, timeZone, (d) => dfAddHours(d, amount));
    }

    function subDays(input, amount) {
      return mapInTz(input, timeZone, (d) => dfSubDays(d, amount));
    }

    function startOfMonth(input) {
      return mapInTz(input, timeZone, (d) => dfStartOfMonth(d));
    }

    function endOfMonth(input) {
      return mapInTz(input, timeZone, (d) => dfEndOfMonth(d));
    }

    function addMonths(input, amount) {
      return mapInTz(input, timeZone, (d) => dfAddMonths(d, amount));
    }

    function addYears(input, amount) {
      return mapInTz(input, timeZone, (d) => dfAddYears(d, amount));
    }

    function subMonths(input, amount) {
      return mapInTz(input, timeZone, (d) => dfSubMonths(d, amount));
    }

    function subYears(input, amount) {
      return mapInTz(input, timeZone, (d) => dfSubYears(d, amount));
    }

    function sub(input, duration) {
      return mapInTz(input, timeZone, (d) => dfSub(d, duration));
    }

    function startOfHour(input) {
      return mapInTz(input, timeZone, (d) => dfStartOfHour(d));
    }

    function isWithinInterval(input, interval) {
      const zDate = toZonedDate(input, timeZone);
      const zStart = toZonedDate(interval.start, timeZone);
      const zEnd = toZonedDate(interval.end, timeZone);

      return dfIsWithinInterval(zDate, { start: zStart, end: zEnd });
    }

    function differenceInCalendarDays(left, right) {
      const zLeft = toZonedDate(left, timeZone);
      const zRight = toZonedDate(right, timeZone);

      return dfDifferenceInCalendarDays(zLeft, zRight);
    }

    function toArray({ startDate, endDate, timeUnit = TIME_UNITS.DAY }) {
      if (!startDate || !endDate) return [];

      const ranges = [];
      const normalizedStart = startOfDay(startDate);
      const rangeStart = (() => {
        switch (timeUnit) {
          case TIME_UNITS.DAY:
            return normalizedStart;
          case TIME_UNITS.WEEK:
            return normalizedStart;
          case TIME_UNITS.MONTH:
            return normalizedStart;
          default:
            throw new Error(`Invalid timeUnit: ${timeUnit}`);
        }
      })();
      const rangeEnd = endOfDay(endDate);

      if (isAfter(rangeStart, rangeEnd)) return [];

      const getNextStart = (current) => {
        switch (timeUnit) {
          case TIME_UNITS.DAY:
            return addDays(current, 1);
          case TIME_UNITS.WEEK:
            return addDays(
              mapInTz(current, timeZone, (tzDate) => startOfWeek(tzDate, { weekStartsOn: 1 })),
              7,
            );
          case TIME_UNITS.MONTH:
            return startOfMonth(addMonths(current, 1));
          default:
            throw new Error(`Invalid timeUnit: ${timeUnit}`);
        }
      };

      const getRangeEnd = (current) => {
        switch (timeUnit) {
          case TIME_UNITS.DAY:
            return endOfDay(current);
          case TIME_UNITS.WEEK:
            return endOfDay(
              addDays(
                mapInTz(current, timeZone, (tzDate) => startOfWeek(tzDate, { weekStartsOn: 1 })),
                6,
              ),
            );
          case TIME_UNITS.MONTH:
            return endOfMonth(current);
          default:
            throw new Error(`Invalid timeUnit: ${timeUnit}`);
        }
      };

      for (let cursor = rangeStart; !isAfter(cursor, rangeEnd); cursor = getNextStart(cursor)) {
        let bucketEnd = getRangeEnd(cursor);
        if (isAfter(bucketEnd, rangeEnd)) {
          bucketEnd = rangeEnd;
        }

        ranges.push({
          startDate: getUnixTime(cursor),
          endDate: getUnixTime(bucketEnd),
        });
      }

      return ranges;
    }

    // Resolve the `recent` preset against an optional lifecycle anchor and clamp
    // it to the feature start when there is less than seven days of data.
    function resolveRecentDateRange({ featureStartedAt, featureEndedAt, relativeTo } = {}) {
      const now = new Date();
      const todayStart = startOfDay(now);
      const todayEnd = endOfDay(now);
      const endAnchor = featureEndedAt ?? relativeTo;

      let endDate = endAnchor ? endOfDay(toDate(endAnchor)) : todayEnd;

      if (isAfter(endDate, todayEnd)) {
        endDate = todayEnd;
      }

      let startBoundary = null;

      if (featureStartedAt) {
        startBoundary = startOfDay(toDate(featureStartedAt));

        if (isAfter(startBoundary, todayStart)) {
          startBoundary = todayStart;
        }

        if (isAfter(startBoundary, endDate)) {
          startBoundary = startOfDay(endDate);
        }
      }

      let startDate = startOfDay(subDays(endDate, 6));

      if (startBoundary && isBefore(startDate, startBoundary)) {
        startDate = startBoundary;
      }

      return { startDate, endDate };
    }

    // Convert a selected range into concrete Unix boundaries and derive the
    // granularity used by charts/queries from the final span.
    function getConvertedDateRange(daterange, { relativeTo, featureStartedAt, featureEndedAt } = {}) {
      let startDate, endDate;

      const now = new Date();
      const endAnchor = featureEndedAt ?? relativeTo;
      const hasAnchorDate = endAnchor != null;
      const anchorDate = hasAnchorDate ? toDate(endAnchor) : now;
      const special = daterange?.special;

      if (special != null) {
        if (!SUPPORTED_SPECIAL_DATE_RANGES.has(special)) {
          throw new RangeError(`Unsupported special date range: ${special}`);
        }

        switch (special) {
          case TODAY:
            startDate = startOfDay(now);
            endDate = endOfDay(now);
            break;
          case YESTERDAY:
            startDate = startOfDay(subDays(now, 1));
            endDate = endOfDay(subDays(now, 1));
            break;
          case RECENT: {
            const recentDateRange = resolveRecentDateRange({
              featureStartedAt,
              featureEndedAt,
              relativeTo,
            });

            startDate = recentDateRange.startDate;
            endDate = recentDateRange.endDate;
            break;
          }
          case LAST_7_DAYS:
            startDate = startOfDay(subDays(anchorDate, hasAnchorDate ? 6 : 7));
            endDate = endOfDay(hasAnchorDate ? anchorDate : subDays(anchorDate, 1));
            break;
          case LAST_30_DAYS:
            startDate = startOfDay(subDays(anchorDate, hasAnchorDate ? 29 : 30));
            endDate = endOfDay(hasAnchorDate ? anchorDate : subDays(anchorDate, 1));
            break;
          case LAST_100_DAYS:
            startDate = startOfDay(subDays(anchorDate, hasAnchorDate ? 99 : 100));
            endDate = endOfDay(hasAnchorDate ? anchorDate : subDays(anchorDate, 1));
            break;
          case THIS_MONTH:
            startDate = startOfMonth(now);
            endDate = endOfDay(now);
            break;
          case LAST_MONTH:
            startDate = startOfMonth(subMonths(now, 1));
            endDate = endOfMonth(subMonths(now, 1));
            break;
          default:
            // `special` is validated against `SUPPORTED_SPECIAL_DATE_RANGES` above.
            throw new Error(`Unreachable special date range: ${special}`);
        }
      } else if (daterange?.start_date != null && daterange?.end_date != null) {
        startDate = toDate(daterange.start_date);
        endDate = toDate(daterange.end_date);
      } else {
        throw new RangeError(
          `Invalid date range input: expected a supported special date range or both start_date and end_date, received ${describeDateRangeInput(daterange)}`,
        );
      }
      startDate = getUnixTime(startDate);
      endDate = getUnixTime(endDate);

      let timeUnit = TIME_UNITS.MONTH;
      if (endDate - startDate < 30 * singleDay) {
        timeUnit = TIME_UNITS.DAY;
      } else if (endDate - startDate < 180 * singleDay) {
        timeUnit = TIME_UNITS.WEEK;
      }

      return {
        startDate,
        endDate,
        timeUnit,
      };
    }

    function getDefaultDateRange({
      featureStartedAt,
      featureEndedAt,
      activeDefault = DEFAULT_DATE_RANGE_POLICIES.RECENT,
      endedDefault = DEFAULT_DATE_RANGE_POLICIES.RECENT,
    } = {}) {
      const defaultPolicy = featureEndedAt ? endedDefault : activeDefault;

      switch (defaultPolicy) {
        case DEFAULT_DATE_RANGE_POLICIES.RECENT:
          return { special: RECENT };
        case DEFAULT_DATE_RANGE_POLICIES.LIFETIME:
          if (!featureStartedAt) {
            return { special: RECENT };
          }

          return {
            start_date: startOfDay(featureStartedAt),
            end_date: endOfDay(featureEndedAt ?? new Date()),
          };
        default:
          throw new RangeError(`Unsupported default date range policy: ${defaultPolicy}`);
      }
    }

    // Normalize semantic feature bounds into the concrete picker/query bounds
    // used by the shared resolver. Callers can still override them explicitly.
    function getDateRangeContext({ featureStartedAt, featureEndedAt, lowerBoundary, upperBoundary } = {}) {
      const normalizedFeatureStartedAt = featureStartedAt != null ? startOfDay(toDate(featureStartedAt)) : undefined;
      const normalizedFeatureEndedAt = featureEndedAt != null ? endOfDay(toDate(featureEndedAt)) : undefined;
      const computedUpper =
        upperBoundary != null ? toDate(upperBoundary) : (normalizedFeatureEndedAt ?? endOfDay(new Date()));
      const computedLowerBoundary = lowerBoundary != null ? toDate(lowerBoundary) : normalizedFeatureStartedAt;

      return {
        featureStartedAt: normalizedFeatureStartedAt,
        featureEndedAt: normalizedFeatureEndedAt,
        lowerBoundary:
          computedLowerBoundary != null && computedUpper != null && computedLowerBoundary > computedUpper
            ? computedUpper
            : computedLowerBoundary,
        upperBoundary: computedUpper,
      };
    }

    function getMaxAllowableRangeMaxRange(maxAllowableRange = MAX_ALLOWABLE_DATE_RANGES.DEFAULT) {
      return maxAllowableRange?.maxRange;
    }

    function getMaxAllowableRangeStart(endDate, maxAllowableRange = MAX_ALLOWABLE_DATE_RANGES.DEFAULT) {
      if (!maxAllowableRange) return null;

      switch (maxAllowableRange.unit) {
        case 'days':
          return startOfDay(subDays(endDate, maxAllowableRange.value - 1));
        case 'years':
          return startOfDay(subYears(endDate, maxAllowableRange.value));
        default:
          throw new RangeError(`Unsupported max allowable range unit: ${maxAllowableRange.unit}`);
      }
    }

    function tryResolveDateRange(dateRange, resolverOptions) {
      try {
        return getConvertedDateRange(dateRange, resolverOptions);
      } catch {
        return null;
      }
    }

    function hasNoOverlapWithBoundaries(
      { start_date: startInput, end_date: endInput },
      { lowerBoundary, upperBoundary } = {},
    ) {
      const startDate = toDate(startInput);
      const endDate = toDate(endInput);

      if (lowerBoundary && isBefore(endDate, lowerBoundary)) {
        return true;
      }

      if (upperBoundary && isAfter(startDate, upperBoundary)) {
        return true;
      }

      return false;
    }

    // Keep an absolute range inside the allowed window and trim from the end so
    // the surviving range stays as recent as possible.
    function clampConcreteDateRange(
      { start_date: startInput, end_date: endInput },
      { lowerBoundary, upperBoundary, maxAllowableRange = MAX_ALLOWABLE_DATE_RANGES.DEFAULT } = {},
    ) {
      const normalizeCollapsedDayRange = (date) => ({
        start_date: startOfDay(date),
        end_date: endOfDay(date),
      });

      let startDate = toDate(startInput);
      let endDate = toDate(endInput);

      if (lowerBoundary && isBefore(startDate, lowerBoundary)) {
        startDate = toDate(lowerBoundary);
      }

      if (lowerBoundary && isBefore(endDate, lowerBoundary)) {
        endDate = endOfDay(lowerBoundary);
      }

      if (upperBoundary && isAfter(startDate, upperBoundary)) {
        startDate = toDate(upperBoundary);
      }

      if (upperBoundary && isAfter(endDate, upperBoundary)) {
        endDate = toDate(upperBoundary);
      }

      if (isAfter(startDate, endDate)) {
        ({ start_date: startDate, end_date: endDate } = normalizeCollapsedDayRange(endDate));
      }

      const maxAllowableRangeStart = getMaxAllowableRangeStart(endDate, maxAllowableRange);

      if (maxAllowableRangeStart && isBefore(startDate, maxAllowableRangeStart)) {
        startDate = maxAllowableRangeStart;
      }

      if (isAfter(startDate, endDate)) {
        ({ start_date: startDate, end_date: endDate } = normalizeCollapsedDayRange(endDate));
      }

      if (startDate.getTime() === endDate.getTime()) {
        ({ start_date: startDate, end_date: endDate } = normalizeCollapsedDayRange(endDate));
      }

      return {
        start_date: startDate,
        end_date: endDate,
      };
    }

    // Resolve a canonical date-range object by applying defaults, resolving
    // specials, clamping to bounds, and normalizing picker state.
    function resolveParsedDateRange({
      dateRange,
      defaultDateRange,
      featureStartedAt,
      featureEndedAt,
      lowerBoundary,
      upperBoundary,
      maxAllowableRange = MAX_ALLOWABLE_DATE_RANGES.DEFAULT,
      resolverOptions,
    } = {}) {
      const attemptedDateRange = dateRange ?? null;
      const selectedDateRange = attemptedDateRange ?? defaultDateRange;

      if (!selectedDateRange) {
        return null;
      }

      const dateRangeContext = getDateRangeContext({
        featureStartedAt,
        featureEndedAt,
        lowerBoundary,
        upperBoundary,
      });
      const effectiveResolverOptions = {
        ...resolverOptions,
        featureStartedAt: resolverOptions?.featureStartedAt ?? dateRangeContext.featureStartedAt,
        featureEndedAt: resolverOptions?.featureEndedAt ?? dateRangeContext.featureEndedAt,
      };

      const attemptedResolvedDateRange = attemptedDateRange
        ? tryResolveDateRange(attemptedDateRange, effectiveResolverOptions)
        : null;
      const defaultResolvedDateRange = defaultDateRange
        ? tryResolveDateRange(defaultDateRange, effectiveResolverOptions)
        : null;
      const attemptedPickerDateRange = attemptedResolvedDateRange
        ? {
            start_date: fromUnixTime(attemptedResolvedDateRange.startDate),
            end_date: fromUnixTime(attemptedResolvedDateRange.endDate),
          }
        : null;
      const shouldFallBackToDefault =
        attemptedPickerDateRange &&
        defaultResolvedDateRange &&
        hasNoOverlapWithBoundaries(attemptedPickerDateRange, {
          lowerBoundary: dateRangeContext.lowerBoundary,
          upperBoundary: dateRangeContext.upperBoundary,
        });
      const resolvedDateRange =
        shouldFallBackToDefault || !attemptedResolvedDateRange ? defaultResolvedDateRange : attemptedResolvedDateRange;

      if (!resolvedDateRange) {
        return null;
      }

      const effectiveSelectedDateRange =
        attemptedResolvedDateRange && !shouldFallBackToDefault ? selectedDateRange : defaultDateRange;
      const resolvedPickerDateRange = {
        start_date: fromUnixTime(resolvedDateRange.startDate),
        end_date: fromUnixTime(resolvedDateRange.endDate),
      };
      const pickerDateRange = clampConcreteDateRange(resolvedPickerDateRange, {
        lowerBoundary: dateRangeContext.lowerBoundary,
        upperBoundary: dateRangeContext.upperBoundary,
        maxAllowableRange,
      });
      const didClamp =
        pickerDateRange.start_date.getTime() !== resolvedPickerDateRange.start_date.getTime() ||
        pickerDateRange.end_date.getTime() !== resolvedPickerDateRange.end_date.getTime();
      const normalizedDateRange =
        effectiveSelectedDateRange?.special && !didClamp
          ? { special: effectiveSelectedDateRange.special }
          : pickerDateRange;

      return {
        selectedDateRange: normalizedDateRange,
        convertedDateRange:
          normalizedDateRange.special && !didClamp
            ? resolvedDateRange
            : getConvertedDateRange(normalizedDateRange, effectiveResolverOptions),
        pickerDateRange,
        pickerProps: {
          lowerBoundary: dateRangeContext.lowerBoundary,
          upperBoundary: dateRangeContext.upperBoundary,
          maxRange: getMaxAllowableRangeMaxRange(maxAllowableRange),
          special: normalizedDateRange.special ?? null,
          startDate: pickerDateRange.start_date,
          endDate: pickerDateRange.end_date,
        },
        didClamp,
        usedDefault: attemptedDateRange == null || effectiveSelectedDateRange !== attemptedDateRange,
      };
    }

    // Wrapper around `resolveParsedDateRange` for raw query-param values that
    // also tells callers whether the URL should be rewritten.
    function resolveDateRangeParam({
      rawValue,
      adapter,
      defaultDateRange,
      featureStartedAt,
      featureEndedAt,
      lowerBoundary,
      upperBoundary,
      maxAllowableRange = MAX_ALLOWABLE_DATE_RANGES.DEFAULT,
      resolverOptions,
    } = {}) {
      const parsed = adapter.parse(rawValue);
      const resolvedDateRange = resolveParsedDateRange({
        dateRange: parsed.status === 'valid' ? parsed.value : null,
        defaultDateRange,
        featureStartedAt,
        featureEndedAt,
        lowerBoundary,
        upperBoundary,
        maxAllowableRange,
        resolverOptions,
      });

      if (!resolvedDateRange) {
        const dateRangeContext = getDateRangeContext({
          featureStartedAt,
          featureEndedAt,
          lowerBoundary,
          upperBoundary,
        });
        return {
          parseState: parsed.status,
          selectedDateRange: null,
          convertedDateRange: null,
          pickerDateRange: null,
          pickerProps: {
            lowerBoundary: dateRangeContext.lowerBoundary,
            upperBoundary: dateRangeContext.upperBoundary,
            maxRange: getMaxAllowableRangeMaxRange(maxAllowableRange),
            special: null,
            startDate: null,
            endDate: null,
          },
          didClamp: false,
          usedDefault: parsed.status !== 'valid',
          shouldRewrite: false,
          serializedValue: null,
        };
      }

      const serializedValue = adapter.serialize(resolvedDateRange.selectedDateRange);

      return {
        ...resolvedDateRange,
        parseState: parsed.status,
        shouldRewrite: parsed.status !== 'missing' && serializedValue !== rawValue,
        serializedValue,
        usedDefault: parsed.status !== 'valid',
      };
    }

    return {
      timeZone: getTimeZoneAbbreviations(timeZone),
      format,
      formatDay,
      formatDayFull,
      formatDateTime,
      formatDateTimeFull,
      formatRangeSameMonth,
      formatRangeSameYear,
      formatRangeDifferentYear,
      formatChartLabel,
      formatChartTooltip,
      isSameDay,
      isSameMonth,
      isSameYear,
      isAfter,
      isBefore,
      startOfDay,
      endOfDay,
      addDays,
      addHours,
      subDays,
      startOfMonth,
      endOfMonth,
      addMonths,
      addYears,
      subMonths,
      subYears,
      sub,
      startOfHour,
      isWithinInterval,
      differenceInCalendarDays,
      toArray,
      getConvertedDateRange,
      getDefaultDateRange,
      getDateRangeContext,
      clampConcreteDateRange,
      resolveParsedDateRange,
      resolveDateRangeParam,
    };
  }, [timeZone]);

  return helpers;
}

function describeDateRangeInput(daterange) {
  if (daterange === undefined) return 'undefined';
  if (daterange === null) return 'null';
  if (typeof daterange !== 'object' || Array.isArray(daterange)) return String(daterange);

  const keys = Object.keys(daterange).sort();
  const hasStartDate = Object.prototype.hasOwnProperty.call(daterange, 'start_date');
  const hasEndDate = Object.prototype.hasOwnProperty.call(daterange, 'end_date');
  const hasSpecial = Object.prototype.hasOwnProperty.call(daterange, 'special');

  return `{ keys: [${keys.join(', ')}], hasSpecial: ${hasSpecial}, special: ${String(daterange.special)}, hasStartDate: ${hasStartDate}, hasEndDate: ${hasEndDate} }`;
}

export function toDate(input) {
  // Already a Date instance: return as-is
  if (input instanceof Date) return input;

  // Numeric: decide seconds vs milliseconds
  if (typeof input === 'number' && Number.isFinite(input)) {
    // seconds if too small to be milliseconds
    const ms = Math.abs(input) < 1e11 ? input * 1000 : input;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) {
      throw new RangeError(`Invalid numeric date input: ${input}`);
    }
    return d;
  }

  // String or other: let Date parse it
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) {
    throw new RangeError(`Invalid date input: ${input}`);
  }
  return d;
}

function isValidTimeZone(tz) {
  if (typeof tz !== 'string') return false;

  const zone = tz.trim();

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

function toZonedDate(input, timeZone) {
  const date = toDate(input);
  return utcToZonedTime(date, timeZone);
}

// Generic “compare two dates in a time zone”
function compareInTz(left, right, timeZone, comparator) {
  const l = toZonedDate(left, timeZone);
  const r = toZonedDate(right, timeZone);
  return comparator(l, r);
}

// Generic “transform one date in a time zone and return the UTC instant”
function mapInTz(input, timeZone, transformer) {
  const zoned = toZonedDate(input, timeZone);
  const localResult = transformer(zoned);
  return zonedTimeToUtc(localResult, timeZone);
}

function getDefaultLocale() {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }

  try {
    const opts = Intl.DateTimeFormat().resolvedOptions();
    if (opts && opts.locale) return opts.locale;
  } catch {
    // noop
  }

  return 'en-US';
}

// Returns: { full: 'Europe/Paris', current: "CEST", primary: "CET" }
// Or for US zones: { full: 'America/New_York', current: "EDT", primary: "ET" }
// Or for zones with no abbreviations: { full: 'Asia/Calcutta', current: "GMT+5:30", primary: "GMT+5:30" }
export function getTimeZoneAbbreviations(timeZone, date = new Date()) {
  let current = null;
  const locale = getDefaultLocale();
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(date);

    const tz = parts.find((p) => p.type === 'timeZoneName');
    current = tz ? tz.value : null;
  } catch {
    return { full: timeZone, current: null, primary: null };
  }

  if (!current) {
    return { full: timeZone, current: null, primary: null };
  }

  // ---- MAKE PRIMARY ABBREVIATION ----

  // Case 1: Classic DST abbreviations (CET / CEST, EST / EDT, etc.)
  // If abbreviation ends with 'T' (-> "CET", "IST", "UTC") we treat it as primary.
  // If it ends with 'ST' or 'DT', drop the last letter ("EST"/"EDT" -> "ET").
  if (/^[A-Z]{2,4}$/.test(current)) {
    if (current.endsWith('DT')) {
      return { full: timeZone, current, primary: current.slice(0, -2) + 'T' }; // EDT -> ET
    }
    if (current.endsWith('ST')) {
      return { full: timeZone, current, primary: current.slice(0, -2) + 'T' }; // EST -> ET
    }
    if (current.endsWith('T')) {
      return { full: timeZone, current, primary: current }; // CET, JST, WET, UTC
    }
  }

  // Case 2: Offset zones like "GMT+5:30", "GMT+9"
  // These zones have no canonical abbreviation; primary = current
  return { full: timeZone, current, primary: current };
}
